import { Decimal } from 'decimal.js';
import { CurrencyCode, getCurrencyDecimalPlaces, NumberFormat } from '@insio/shared-types';
import { DEFAULT_PLACEHOLDER } from '@insio/format-utils';

export interface FormatAmountOptions {
  /** ISO 4217 currency code used to pick decimal precision (e.g. EUR → 2, JPY → 0). */
  currencyCode?: CurrencyCode | null;
  /** BCP 47 locale for grouping separators and decimal mark. Falls back to runtime default. */
  locale?: string;
  /** Include grouping separators (e.g. "1,234.56"). Defaults to true; set false for inputs. */
  useGrouping?: boolean;
  numberFormat?: string;
  /**
   * When true, formats the number as a percentage - adds a "%" sign (e.g. 12.3 → "12.3%").
   */
  addPercentSign?: boolean;
  /** Decimal places to use instead of the currency default. */
  decimalPlaces?: number;
  /** Placeholder to return when the amount is null/undefined/NaN. Defaults to `DEFAULT_PLACEHOLDER` from `@insio/format-utils`. */
  placeholder?: string;

  /**
   * Overrides the number of decimal places determined by `currencyCode` or `decimalPlaces`
   * and formats the number with that many decimals. This is useful for percentages, which often
   * require a different number of decimals than the currency precision (e.g. "12.3%" instead of "12.30%").
   */
  minimumFractionDigits?: number;
  /**
   * Overrides the number of decimal places determined by `currencyCode` or `decimalPlaces`
   * and formats the number with that many decimals. This is useful for percentages, which often
   * require a different number of decimals than the currency precision (e.g. "12.3%" instead of "12.30%").
   */
  maximumFractionDigits?: number;
}

function formatToLocale(numberFormat: NumberFormat | undefined | null): string {
  if (!numberFormat) {
    return 'cs-CZ';
  }
  switch (numberFormat) {
    case 'COMMA_SPACE':
      return 'cs-CZ';
    case 'COMMA_PERIOD':
      return 'de-DE';
    case 'PERIOD_COMMA':
      return 'us-US';
    default:
      return 'cs-CZ';
  }
}

/**
 * Formats a `Decimal` as a currency-precision number string. Trailing zeros are
 * preserved (e.g. EUR `1` → "1.00"). When `locale` is provided the output uses
 * locale-aware grouping and decimal mark via `Intl.NumberFormat`.
 */
export function formatAmount(
  amount: Decimal | null | undefined,
  options: FormatAmountOptions = {}
): string {
  if (amount == null || amount.isNaN()) {
    return options?.placeholder ?? DEFAULT_PLACEHOLDER;
  }
  const { currencyCode, numberFormat, useGrouping = true, decimalPlaces } = options;
  const decimals = decimalPlaces ?? (currencyCode ? getCurrencyDecimalPlaces(currencyCode) : 2);
  const rounded = amount.toDecimalPlaces(decimals);

  const locale = options.locale || formatToLocale(numberFormat as NumberFormat);
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: options.minimumFractionDigits ?? decimals,
    maximumFractionDigits: options.maximumFractionDigits ?? decimals,
    useGrouping
  }).format(rounded.toNumber());

  return options.addPercentSign ? `${formatted} %` : formatted;
}
